import React, { useState } from "react";
import { Button, TextField, Typography, DialogTitle, DialogContent, IconButton, CircularProgress, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import configs from "../../Constants";
import PlaceAutocomplete from "./PlaceAutocomplete";

// Haversine formula to calculate distance in km between two lat/lng points
const getDistance = (lat1, lng1, lat2, lng2) => {
  if (!lat1 || !lng1 || !lat2 || !lng2) return null;
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return (R * c).toFixed(1);
};

const CustomerSelectionPopup = ({
  merchantData,
  order,
  setOrder,
  closeDialog
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [custId, setCustId] = useState("");
  const [phnumber, setPhnumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [customerLat, setCustomerLat] = useState("");
  const [customerLng, setCustomerLng] = useState("");
  


  const handleSearchCustomer = async () => {
    if (!phnumber) return;
    setIsLoading(true);
    const userToken = localStorage.getItem("token") || "";
    try {
      setSearchAttempted(true);
      const res = await axios.post(
        `${configs.authapi}/user/customers/find`,
        { phone: phnumber },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      const customers = Array.isArray(res.data?.customers) ? res.data.customers : [];
      setSearchResults(customers);
      if (customers.length > 0) {
        const firstCustomer = customers[0];
        setName(firstCustomer.firstName || "");
        setEmail(firstCustomer.email || "");
        setAddress(firstCustomer.address || "");
        setCustId(firstCustomer.id || firstCustomer._id);
        if (firstCustomer.latitude) setCustomerLat(firstCustomer.latitude);
        if (firstCustomer.longitude) setCustomerLng(firstCustomer.longitude);
        else if (firstCustomer.location && firstCustomer.location.coordinates) {
          // GeoJSON stores [longitude, latitude]
          setCustomerLat(firstCustomer.location.coordinates[1]);
          setCustomerLng(firstCustomer.location.coordinates[0]);
        }
      }
    } catch (err) {
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomerSelect = (customer) => {
    setName(customer.firstName);
    setEmail(customer.email);
    setPhnumber(customer.phone);
    setCustId(customer.id);
    setAddress(customer.address);
    if (customer.latitude) setCustomerLat(customer.latitude);
    if (customer.longitude) setCustomerLng(customer.longitude);
    else if (customer.location && customer.location.coordinates) {
      // GeoJSON stores [longitude, latitude]
      setCustomerLat(customer.location.coordinates[1]);
      setCustomerLng(customer.location.coordinates[0]);
    }
  };

  const handleSaveCustomerSelection = () => {
    if (phnumber) {
      const updatedOrder = {
        ...(order || {}),
        customerId: custId,
        customerPhone: phnumber,
        customerName: name !== "" ? name : phnumber,
        customerAddress: address,
        customerLat: customerLat,
        customerLng: customerLng,
        orderItems: order?.orderItems || [],
      };
      setOrder(updatedOrder);
      closeDialog();
    }
  };

  const handleAddCustomer = async () => {
    if (!phnumber) return;
    setIsLoading(true);
    const userToken = localStorage.getItem("token") || "";
    const generatedEmail = email || `${phnumber}@nodomain.com`;
    try {
      const data = {
        email: generatedEmail,
        phone: phnumber,
        firstName: name || phnumber,
        lastName: "",
        address: address || "",
        password: phnumber,
        isEmailVerified: false,
        isPhoneVerified: false,
        referenceDetails: "",
        merchantCode: merchantData.merchantCode,
        userType: "CUSTOMER",
        latitude: customerLat,
        longitude: customerLng,
      };
      const res = await axios.post(`${configs.authapi}/user/customer`, data, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      let newCustId = custId;
      if (res?.data?.user?.customer?.id) {
        newCustId = res.data.user.customer.id;
        setCustId(newCustId);
      }
      
      const updatedOrder = {
        ...(order || {}),
        customerId: newCustId,
        customerPhone: phnumber,
        customerName: name !== "" ? name : phnumber,
        customerAddress: address,
        customerLat: customerLat,
        customerLng: customerLng,
        orderItems: order?.orderItems || [],
      };
      setOrder(updatedOrder);
      closeDialog();

    } catch (error) {
      console.log("Error adding customer:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <b>Customer Selection</b>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={closeDialog}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 3 }}>
        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Find Mobile Number"
            value={phnumber}
            onChange={(e) => setPhnumber(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            inputProps={{ maxLength: 15, pattern: "[0-9]*" }}
            onKeyPress={(e) => e.key === 'Enter' && handleSearchCustomer(e)}
            autoFocus
          />
          <Button
            variant="contained"
            onClick={handleSearchCustomer}
            disabled={!phnumber || isLoading}
            sx={{ 
              minWidth: "80px", 
              bgcolor: "#000", 
              color: "#fff",
              '&:hover': { bgcolor: '#333' },
              '&.Mui-disabled': {
                bgcolor: isLoading ? '#333' : 'rgba(0, 0, 0, 0.12)',
                color: isLoading ? '#fff' : 'rgba(0, 0, 0, 0.26)'
              }
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : <SearchIcon />}
          </Button>
        </Box>

        {searchAttempted && searchResults.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Select Existing Customer:</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {searchResults.map((customer) => (
                <Box
                  key={customer.id || customer._id}
                  sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    cursor: "pointer", 
                    p: 1.5, 
                    border: "1px solid #e0e0e0", 
                    borderRadius: 1,
                    bgcolor: (customer.id === custId || customer._id === custId) ? '#f5f5f5' : 'transparent',
                    '&:hover': { bgcolor: '#f9f9f9' }
                  }}
                  onClick={() => handleCustomerSelect(customer)}
                >
                  <input
                    type="radio"
                    checked={customer.id === custId || customer._id === custId}
                    onChange={() => handleCustomerSelect(customer)}
                    id={customer.id || customer._id}
                    style={{ cursor: "pointer", margin: 0, marginRight: '12px', width: '18px', height: '18px' }}
                  />
                  <Typography component="label" htmlFor={customer.id || customer._id} sx={{ cursor: "pointer", flex: 1, m: 0 }}>
                    <strong>{customer.firstName}</strong> - {customer.phone}
                    {customer.address && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {customer.address}
                        {(() => {
                          const custLat = customer.latitude || customer.location?.coordinates?.[1];
                          const custLng = customer.longitude || customer.location?.coordinates?.[0];
                          const merchantLat = merchantData?.latitude || merchantData?.location?.coordinates?.[1];
                          const merchantLng = merchantData?.longitude || merchantData?.location?.coordinates?.[0];
                          const dist = getDistance(merchantLat, merchantLng, custLat, custLng);
                          return dist ? (
                            <Typography component="span" variant="caption" sx={{ display: "block", color: "primary.main", fontWeight: 500, mt: 0.25 }}>
                              📍 {dist} km away
                            </Typography>
                          ) : null;
                        })()}
                      </Typography>
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Delivery address — read-only from search result */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Delivery Address:
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ mt: 0.25 }}>
                {address || <em style={{ color: '#aaa' }}>No address on record</em>}
              </Typography>
            </Box>
          </Box>
        )}

        {searchAttempted && searchResults.length === 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ color: "error.main", mb: 2, fontSize: "0.875rem" }}>
              No Customer found with this number. Please Add as New customer.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                type="text"
                label="Customer Name"
                placeholder="Enter Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
              />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Delivery Address</Typography>
                <PlaceAutocomplete
                  apiKey="AIzaSyDFUUvF4IKg-pTy_Lmhn1_Q9Dh_4f-uHI8"
                  onPlaceSelected={(place) => {
                    setAddress(place.formatted_address || place.name || "");
                    setCustomerLat(place.geometry?.location?.lat());
                    setCustomerLng(place.geometry?.location?.lng());
                  }}
                  options={{
                    types: ["address"],
                  }}
                  defaultValue={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Delivery Address"
                  style={{
                    width: "100%"
                  }}
                />
                {customerLat && customerLng && (() => {
                  // GeoJSON stores [longitude, latitude]
                  const merchantLat = merchantData?.latitude || merchantData?.location?.coordinates?.[1];
                  const merchantLng = merchantData?.longitude || merchantData?.location?.coordinates?.[0];
                  const dist = getDistance(merchantLat, merchantLng, customerLat, customerLng);
                  return dist ? (
                    <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 500, mt: 0.5 }}>
                      📍 {dist} km away from restaurant
                    </Typography>
                  ) : null;
                })()}
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", pt: 3, justifyContent: "flex-end", gap: 2 }}>
          {searchAttempted && searchResults.length === 0 && (
            <Button 
              variant="contained" 
              color="success" 
              onClick={handleAddCustomer} 
              disabled={isLoading} 
              fullWidth
              sx={{
                '&.Mui-disabled': {
                  bgcolor: isLoading ? 'success.main' : 'rgba(0, 0, 0, 0.12)',
                  color: isLoading ? '#fff' : 'rgba(0, 0, 0, 0.26)',
                  opacity: isLoading ? 0.7 : 1
                }
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : "SAVE CUSTOMER"}
            </Button>
          )}

          {searchAttempted && searchResults.length > 0 && (
            <Button
              variant="contained"
              onClick={handleSaveCustomerSelection}
              sx={{ bgcolor: "#f7c919", color: '#000', fontWeight: 'bold', '&:hover': { bgcolor: '#e0b516' }, px: 5 }}
            >
              SELECT
            </Button>
          )}
        </Box>
      </DialogContent>
    </div>
  );
};

export default CustomerSelectionPopup;
